import type { MouseEvent } from "react";
import { styled } from "styled-components";

import { CheckIcon, PlusIcon } from "common/components/icons";
import { Spinner, UIButton } from "common/components/index";
import { useTranslate } from "common/utilities/useTranslate";

const StyledContainer = styled(UIButton)<{ isFollowing: boolean }>`
  background-color: ${({ theme, isFollowing }) => (isFollowing ? theme.colors.buttonFollowPrimary : "transparent")};
  border-style: solid;
  border-width: 2px;
  border-color: ${({ theme }) => theme.colors.buttonFollowPrimary};
  color: ${({ theme, isFollowing }) =>
    isFollowing ? theme.colors.buttonFollowSecondary : theme.colors.buttonFollowPrimary};
  transition:
    background-color 0.3s,
    color 0.3s;

  &:focus-visible,
  &:hover {
    background: ${({ theme }) => theme.colors.buttonOutlinedActiveBackground};
    color: ${({ theme }) => theme.colors.buttonFollowPrimary};
  }
`;

const StyledInteractiveContainer = styled(StyledContainer)`
  padding: 4px;
  font-weight: bold;
  height: fit-content;

  /* Safari hack to force it to render on top of the image, see TopicTile */
  transform: translate3d(0, 0, 0);

  &:hover {
    padding: 4px 10px;
  }
`;

const StyledOpenContainer = styled(StyledContainer)`
  border-radius: 32px;
  padding: 0.5rem 0.5rem 0.5rem 1.5rem;
  height: fit-content;

  > svg {
    margin-left: 5px;
  }
`;

const StyledFollowText = styled.span`
  font-size: 0;
  margin-right: 0;
  opacity: 0;
  transform: translate3d(0, 33%, 0);
  width: 0;
  transition:
    font-size 0.2s ease,
    margin-right 0.2s ease,
    opacity 0.3s 0.1s ease,
    transform 0.3s 0.1s ease,
    width 0.2s;

  @media (hover: hover) {
    ${StyledInteractiveContainer}:hover & {
      font-size: 0.9rem;
      padding: 0 14px 0 6px;
      opacity: 1;
      transform: translate3d(0, 0%, 0);
      line-height: 16px;
      width: auto;
    }
  }
`;

const StyledFollowButton = styled(StyledContainer)`
  padding: 0;
  width: 40px;
  min-width: 40px;
`;

const StyledUnfollowIcon = styled(CheckIcon)`
  width: 12px;
  height: 12px;
  fill: none;
  padding: 7px;
`;

const StyledFollowIcon = styled(PlusIcon)`
  width: 14px;
  height: 14px;
  padding: 7px;
`;

const StyledLoadingWrapper = styled.div`
  padding: 7px;
  color: ${({ theme }) => theme.colors.buttonFollowPrimary};
`;

const StyledSpinner = styled(Spinner)`
  width: 22px;
  height: 22px;
  padding: 3px;
`;

type UIFollowButtonProps = {
  className?: string;
  isFollowing: boolean;
  isLoading?: boolean;
  onClick: ((event: MouseEvent) => void) | ((event: MouseEvent) => Promise<void>);
  title?: string;
  variant: "collapsed" | "open" | "interactive";
};

/*
  use in follow button in
   - topic tile
      interactive variant
      not themed
   - collections
      open variant
      themed
   - monitor page
      collapsed variant
      not themed
 */

export const UIFollowButton = ({ className, isFollowing, isLoading, onClick, title, variant }: UIFollowButtonProps) => {
  const translate = useTranslate();

  const followText = isFollowing ? translate("app.topic.following") : translate("app.topic.follow");

  const icon = isLoading ? <StyledSpinner /> : isFollowing ? <StyledUnfollowIcon /> : <StyledFollowIcon />;

  const onClickWithoutPropagation = (event: MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    if (!isLoading) void onClick(event);
  };

  if (variant === "interactive") {
    return (
      <StyledInteractiveContainer
        title={title}
        isFollowing={isFollowing}
        onClick={onClickWithoutPropagation}
        disabled={isLoading}
        className={className}
      >
        <StyledFollowText>{followText}</StyledFollowText>
        {icon}
      </StyledInteractiveContainer>
    );
  }

  if (variant === "open") {
    return (
      <StyledOpenContainer
        title={title}
        isFollowing={isFollowing}
        onClick={onClickWithoutPropagation}
        disabled={isLoading}
        className={className}
      >
        {followText}
        {icon}
      </StyledOpenContainer>
    );
  }

  if (isLoading) {
    return <StyledLoadingWrapper onClick={onClickWithoutPropagation}>{icon}</StyledLoadingWrapper>;
  }

  return (
    <StyledFollowButton
      title={title}
      isFollowing={isFollowing}
      onClick={onClickWithoutPropagation}
      disabled={isLoading}
      className={className}
    >
      {icon}
    </StyledFollowButton>
  );
};
